<template>
  <div class="full-height">
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div v-if="isSuccess" class="full-height">
        <TableAlert v-if="items.length" />

        <div v-if="items.length" class="table">
          <div v-if="isValidObject(pagination)" class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber }}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong> {{ $t('Common.policies') }}
          </div>

          <v-layout class="table__header" align-center>
            <v-flex
              v-if="
                claims.timePolicy['ROLE_PUT_TIMEPOLICY_TIMEPOLICYID'] ||
                claims.timePolicy['ROLE_DELETE_TIMEPOLICY_TIMEPOLICYID']
              "
              style="max-width: 38px;"
              xs1
            />
            <v-flex xs5>{{ $t('Common.name') }}</v-flex>
            <v-flex xs6>{{ $t('Common.description') }}</v-flex>
          </v-layout>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <v-list-tile
                class="table__body__tr"
                :class="{ 'table__body__tr--active': activateLine(item) }"
                @click="handleClick($event, item)"
              >
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex
                        v-if="
                          claims.timePolicy['ROLE_PUT_TIMEPOLICY_TIMEPOLICYID'] ||
                          claims.timePolicy['ROLE_DELETE_TIMEPOLICY_TIMEPOLICYID']
                        "
                        style="max-width: 38px;"
                        xs1
                      >
                        <v-menu offset-y right>
                          <button class="table-body__actions" slot="activator">
                            <v-icon>more_vertical</v-icon>
                          </button>

                          <v-list class="dropdown-menu-actions__list">
                            <v-list-tile
                              v-if="claims.timePolicy['ROLE_PUT_TIMEPOLICY_TIMEPOLICYID']"
                              :to="{
                                name: 'UsagePolicy',
                                params: {
                                  type: 'editar',
                                  id: item.id
                                }
                              }"
                            >
                              {{ $t('Common.edit') }}
                            </v-list-tile>

                            <v-divider v-if="claims.timePolicy['ROLE_PUT_TIMEPOLICY_TIMEPOLICYID'] && claims.timePolicy['ROLE_DELETE_TIMEPOLICY_TIMEPOLICYID']" />

                            <v-list-tile
                              v-if="claims.timePolicy['ROLE_DELETE_TIMEPOLICY_TIMEPOLICYID']"
                              @click="handleSingleDeleteDialog(item)"
                            >
                              {{ $t('Common.delete') }}
                            </v-list-tile>
                          </v-list>
                        </v-menu>
                      </v-flex>

                      <v-flex xs5>
                        <span :title="item.name" class="text--ellipsis">
                          {{ item.name || '-' }}
                        </span>
                      </v-flex>

                      <v-flex xs6>
                        <span :title="item.description" class="text--ellipsis">
                          {{ item.description || '-' }}
                        </span>
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="cost-center"
          :text="$t(`${namespace}.emptyStateMessage`)"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>

    <GlobalDrawer
      :show="globalDrawer.visibility"
      :drawerType="globalDrawer.type"
      :data="globalDrawer.data"
      :filters="globalDrawer.filters"
      @close="closeGlobalDrawer"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { isObject, formatNumber } from 'utils/functions'
import TableAlert from './TableAlert'
import EmptyState from 'components/EmptyState'
import GlobalDrawer from 'components/globalDrawer'

export default {
  name: 'Table',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    },
    drawerVisibility: {
      type: Boolean
    }
  },
  components: {
    TableAlert,
    EmptyState,
    GlobalDrawer
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany,
      claims: ({ userClaims }) => userClaims.claims
    }),

    routeId () {
      const { id } = this.$route.params
      return id ? Number(id) : null
    },

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.UsagePoliciesPage',
      selectedItem: null,
      globalDrawer: {
        visibility: false,
        type: '',
        data: {},
        filters: {}
      }
    }
  },
  methods: {
    isValidObject (val) {
      return isObject(val)
    },

    handlePage (page) {
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    activateLine (item) {
      return item.id === this.selectedItem
    },

    handleSingleDeleteDialog (item) {
      this.$emit('deleteItem', item)
    },

    openGlobalDrawer ({ data = {}, filters = {} }) {
      this.globalDrawer = {
        visibility: true,
        type: 'timePolicy',
        data
      }
    },

    closeGlobalDrawer (reload = null) {
      this.globalDrawer = {
        visibility: false,
        type: '',
        data: {},
        filters: {}
      }

      this.selectedItem = null
      reload && this.$emit('reloadData')
    },

    openDrawerIfIdExist () {
      if (this.routeId) {
        this.openGlobalDrawer({
          data: {
            id: this.routeId
          }
        })

        this.selectedItem = this.routeId
      }
    },

    handleClick ($event, item) {
      const cls = $event.target.classList

      if (
        !cls.contains('v-btn__content') &&
        !cls.contains('v-icon') &&
        !cls.contains('v-input--selection-controls__ripple') &&
        !cls.contains('accent--text')
      ) {
        this.selectedItem = item.id

        this.openGlobalDrawer({
          data: {
            id: item.id,
            name: item.name
          }
        })
      }
    }
  },
  watch: {
    selectedCompany () {
      this.closeGlobalDrawer()
    },

    drawerVisibility () {
      this.closeGlobalDrawer()
    }
  },
  mounted () {
    this.openDrawerIfIdExist()
  }
}
</script>
